import { getProductionPartnersType } from '@/utils/product/checkAttribute';
import { getCategoryChildren } from '@/utils/taxonomy/ebay/categories';
import { usedMoreThanAllowed } from '@/utils/validations/utils';
import { getSize } from '@/utils/iterable/getSize';
import { get } from '@/utils/iterable/get';

import { ERRORS, MAX_CONDITION_DESCRIPTION_LENGTH, MAX_PRODUCT_TYPE_LENGTH, MAX_SECTION_LENGTH, MAX_VENDOR_LENGTH } from '@/constants/validations';
import { ABOUT_LIST, AMOUNT, PRODUCTION_PARTNERS_TYPE, VALUE } from '@/constants/product';
import { EBAY, ETSY } from '@/constants/channels';
import { ATTRIBUTE } from '@/constants/attributes';
import { DATA } from '@/data';

export function getWhoMadeError({ channel, value }) {
  switch (channel) {
    case ETSY: {
      return (
        !ABOUT_LIST[channel][ATTRIBUTE.WHO_MADE].includes(value) &&
        ERRORS.DETAILS.ABOUT[ATTRIBUTE.WHO_MADE]
      );
    }

    default: {
      return false;
    }
  }
}

export function getIsSupplyError({ channel, value }) {
  switch (channel) {
    case ETSY: {
      return (
        !ABOUT_LIST[channel][ATTRIBUTE.IS_SUPPLY].includes(value) &&
        ERRORS.DETAILS.ABOUT[ATTRIBUTE.IS_SUPPLY]
      );
    }

    default: {
      return false;
    }
  }
}

export function getWhenMadeError({ channel, value }) {
  switch (channel) {
    case ETSY: {
      return (
        !ABOUT_LIST[channel][ATTRIBUTE.WHEN_MADE].includes(value) &&
        ERRORS.DETAILS.ABOUT[ATTRIBUTE.WHEN_MADE]
      );
    }

    default: {
      return false;
    }
  }
}

export function getCategoryError({ category, channel, marketplaceId }) {
  switch (channel) {
    case EBAY: {
      if (!category || category === AMOUNT.ZERO || getSize(getCategoryChildren({ category, marketplaceId })) > 0) {
        return ERRORS.DETAILS.CATEGORY;
      }

      return false;
    }

    case ETSY: {
      if (!category || category === AMOUNT.ZERO) {
        return ERRORS.DETAILS.CATEGORY;
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

export function getSectionError(value) {
  if (!value) {
    return false;
  } else if (value.length > MAX_SECTION_LENGTH) {
    return ERRORS.DETAILS.SECTION.LENGTH_EXCEEDED;
  } else if (/[\^`]/.test(value)) {
    return ERRORS.DETAILS.SECTION.FORBIDDEN_CHARACTERS;
  } else if (/\p{Extended_Pictographic}/u.test(value)) {
    return ERRORS.DETAILS.SECTION.EMOJIS_NOT_ALLOWED;
  } else if (usedMoreThanAllowed({ patterns: [/\b\p{Lu}{2,}\b/], number: 3, value })) {
    return ERRORS.DETAILS.SECTION.CAPITALIZED_WORDS_NUMBER_EXCEEDED;
  } else if (usedMoreThanAllowed({ patterns: [/[&]/, /[%]/, /[:]/], number: 1, value })) {
    return ERRORS.DETAILS.SECTION.SPECIAL_CHARACTERS_NUMBER_EXCEEDED;
  } else {
    return false;
  }
}

export function getProductTypeError(value) {
  return value && value.length > MAX_PRODUCT_TYPE_LENGTH && ERRORS.DETAILS.PRODUCT_TYPE;
}

export function getVendorError(value) {
  return value && value.length > MAX_VENDOR_LENGTH && ERRORS.DETAILS.VENDOR;
}

export function getProductionPartnersError({ isSupply, productionPartners, whenMade, whoMade }) {
  const type = getProductionPartnersType({ isSupply, whenMade, whoMade });

  if (type === PRODUCTION_PARTNERS_TYPE.REQUIRED && getSize(productionPartners) === 0) {
    return ERRORS.DETAILS.PRODUCTION_PARTNERS.REQUIRED;
  }

  return false;
}

export function getConditionError({ category, channel, condition, marketplaceId }) {
  switch (channel) {
    case EBAY: {
      const conditions = get([EBAY, marketplaceId, 'CATEGORIES', category, 'conditions'])(DATA) || {};

      if (conditions.required) {
        if (getSize(conditions.ids) > 0 && !conditions.byId[condition.get(VALUE)]) return ERRORS.DETAILS.CONDITION_ID;
      }

      if (
        condition.get(VALUE) &&
        condition.get(VALUE) !== '1000' &&
        getSize(condition.get(ATTRIBUTE.DESCRIPTION)) > MAX_CONDITION_DESCRIPTION_LENGTH[EBAY]
      ) {
        return ERRORS.DETAILS.CONDITION_DESCRIPTION[EBAY];
      }

      return false;
    }

    default: {
      return false;
    }
  }
}
